import React, { useCallback } from "react";
import AppContext from "components/AppContext";
import CertificateService from "services/CertificateService";
import Certificate from "interfaces/Certificate";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/Download";
import { downloadPDF } from "services/DownloadPDF";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  AppBar,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  Tooltip,
  IconButton,
  CircularProgress,
  Box,
  Pagination,
} from "@mui/material";

const PAGE_SIZE = 10;

export default function Certificates() {
  const context = React.useContext(AppContext);
  const [inputText, setInputText] = React.useState("");
  const [certificates, setCertificates] = React.useState<Certificate[]>([]);
  const [page, setPage] = React.useState(1);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const navigate = useNavigate();

  const buildFilter = useCallback(
    () =>
      JSON.stringify({
        where: {
          and: [{ name: { like: inputText, options: "i" } }],
        },
        order: "name ASC",
      }),
    [inputText]
  );

  const fetchList = useCallback(
    (page: number) => {
      setLoading(true);
      const f = buildFilter();
      Promise.all([
        CertificateService.getCertificates(f, {
          page,
          pageSize: PAGE_SIZE,
        }),
        CertificateService.countCertificates(f),
      ])
        .then(([data, count]) => {
          setCertificates(data);
          setTotal(count);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    },
    [buildFilter]
  );

  React.useEffect(() => {
    context.changeHeaderTitle("Gestión de certificados");
    fetchList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const search = () => {
    setPage(1);
    fetchList(1);
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    fetchList(value);
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <Paper sx={{ maxWidth: 936, margin: "auto", overflow: "hidden" }}>
        <AppBar
          position="static"
          color="default"
          elevation={0}
          sx={{ borderBottom: "1px solid rgba(0, 0, 0, 0.12)" }}
        >
          <Toolbar>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <IconButton onClick={search}>
                  <SearchIcon color="inherit" sx={{ display: "block" }} />
                </IconButton>
              </Grid>
              <Grid item xs>
                <TextField
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && search()}
                  fullWidth
                  placeholder="Buscar por nombre"
                  InputProps={{
                    disableUnderline: true,
                    sx: { fontSize: "default" },
                  }}
                  variant="standard"
                />
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  sx={{ mr: 1 }}
                  onClick={() => navigate("/panel/certificate/new")}
                >
                  Nuevo Certificado
                </Button>
                <Tooltip title="Actualizar">
                  <IconButton onClick={() => fetchList(page)}>
                    <RefreshIcon color="inherit" sx={{ display: "block" }} />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Toolbar>
        </AppBar>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : certificates.length > 0 ? (
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Usuario Creador</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {certificates.map((certificate) => (
                  <TableRow
                    key={certificate.id}
                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      {certificate.name}
                    </TableCell>
                    <TableCell component="th" scope="row">
                      {certificate.userFirstName || "Carlos"},{" "}
                      {certificate.userLastName || "Flores"}
                    </TableCell>
                    <TableCell component="th" scope="row" align="right">
                      <Tooltip title="Descargar Certificado">
                        <IconButton
                          onClick={() => downloadPDF(certificate)}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton
                          onClick={() =>
                            navigate(`/panel/certificate/${certificate.id}`)
                          }
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography
            sx={{ my: 5, mx: 2 }}
            color="text.secondary"
            align="center"
          >
            No hay certificados
          </Typography>
        )}
      </Paper>
      {total > 0 && (
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {total} registros
          </Typography>
          <Pagination
            count={pageCount}
            page={page}
            onChange={handlePageChange}
            color="primary"
            disabled={loading}
          />
        </Box>
      )}
    </div>
  );
}
